import { ILLUSTRATION as C } from "./IllustrationFrame";

/**
 * Immersive marketplace yard: multiple pro machines around a large circular
 * buy/sell exchange symbol — instantly reads as occasion trading.
 */
export function MarketplaceIllustration({ className = "" }) {
  return (
    <svg
      viewBox="0 0 720 460"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`h-full w-full ${className}`}
      role="img"
      aria-label="Marché de matériel professionnel d'occasion"
    >
      <defs>
        <filter id="mkt-soft" x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="0" dy="12" stdDeviation="14" floodColor="#000" floodOpacity="0.3" />
        </filter>
        <linearGradient id="mkt-ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={C.greenMid} />
          <stop offset="100%" stopColor={C.green} />
        </linearGradient>
      </defs>

      {/* Yard ground */}
      <ellipse cx="360" cy="418" rx="300" ry="26" fill="#000" opacity="0.22" />
      <ellipse cx="360" cy="400" rx="280" ry="48" fill={C.asphalt} opacity="0.55" />
      <ellipse cx="360" cy="400" rx="200" ry="28" fill={C.anthraciteSoft} opacity="0.45" />

      {/* —— Machines around the circle —— */}

      {/* Mini-excavator (left) */}
      <g transform="translate(48 210)" filter="url(#mkt-soft)">
        <rect x="48" y="118" width="130" height="24" rx="12" fill={C.anthracite} />
        <rect x="58" y="126" width="110" height="8" rx="4" fill={C.muted} opacity="0.5" />
        <rect x="70" y="72" width="96" height="54" rx="14" fill={C.green} />
        <rect x="70" y="72" width="96" height="16" rx="14" fill={C.greenDark} />
        <rect x="118" y="46" width="52" height="42" rx="10" fill={C.paper} stroke={C.line} strokeWidth="2.5" />
        <rect x="128" y="56" width="32" height="18" rx="4" fill={C.sky} />
        <path d="M78 92 L18 42 L38 34 L98 80 Z" fill={C.greenDark} />
        <path d="M18 42 L-28 88 L-10 98 L38 48 Z" fill={C.green} />
        <path d="M-42 82 L-10 98 L-14 114 L-48 100 Z" fill={C.anthraciteSoft} stroke={C.anthracite} strokeWidth="2" />
        <circle cx="158" cy="98" r="5" fill={C.greenMid} />
      </g>

      {/* Compact loader / skid steer (bottom-left) */}
      <g transform="translate(110 310) scale(0.92)" filter="url(#mkt-soft)">
        <rect x="20" y="40" width="100" height="48" rx="12" fill={C.greenDark} />
        <rect x="48" y="18" width="56" height="40" rx="10" fill={C.paper} stroke={C.line} strokeWidth="2" />
        <rect x="56" y="28" width="40" height="16" rx="4" fill={C.sky} />
        <rect x="0" y="52" width="28" height="36" rx="6" fill={C.anthraciteSoft} />
        <circle cx="40" cy="96" r="16" fill={C.anthracite} />
        <circle cx="40" cy="96" r="6" fill={C.line} />
        <circle cx="100" cy="96" r="16" fill={C.anthracite} />
        <circle cx="100" cy="96" r="6" fill={C.line} />
      </g>

      {/* Aerial lift / nacelle (top-right) */}
      <g transform="translate(480 88)" filter="url(#mkt-soft)">
        {/* Basket */}
        <rect x="78" y="0" width="70" height="36" rx="8" fill={C.paper} stroke={C.line} strokeWidth="2.5" />
        <rect x="86" y="8" width="54" height="12" rx="3" fill={C.sky} />
        {/* Boom */}
        <path d="M90 36 L40 140 L58 148 L108 44 Z" fill={C.green} />
        <path d="M40 140 L20 168 L40 176 L58 148 Z" fill={C.greenDark} />
        {/* Base truck */}
        <rect x="0" y="168" width="120" height="36" rx="10" fill={C.greenDark} />
        <rect x="80" y="152" width="48" height="40" rx="8" fill={C.anthraciteSoft} />
        <circle cx="28" cy="210" r="14" fill={C.anthracite} />
        <circle cx="28" cy="210" r="5" fill={C.line} />
        <circle cx="88" cy="210" r="14" fill={C.anthracite} />
        <circle cx="88" cy="210" r="5" fill={C.line} />
      </g>

      {/* Tractor (right) */}
      <g transform="translate(520 250)" filter="url(#mkt-soft)">
        <rect x="40" y="48" width="90" height="50" rx="14" fill={C.green} />
        <rect x="40" y="48" width="90" height="14" rx="14" fill={C.greenDark} />
        <rect x="78" y="22" width="48" height="42" rx="10" fill={C.paper} stroke={C.line} strokeWidth="2.5" />
        <rect x="86" y="32" width="32" height="18" rx="4" fill={C.sky} />
        <rect x="0" y="58" width="48" height="28" rx="8" fill={C.greenDark} />
        {/* Large rear wheel */}
        <circle cx="36" cy="108" r="28" fill={C.anthracite} />
        <circle cx="36" cy="108" r="12" fill={C.muted} />
        <circle cx="36" cy="108" r="5" fill={C.line} />
        {/* Front wheel */}
        <circle cx="110" cy="112" r="16" fill={C.anthracite} />
        <circle cx="110" cy="112" r="6" fill={C.line} />
        <rect x="52" y="70" width="24" height="8" rx="2" fill={C.greenMid} opacity="0.8" />
      </g>

      {/* Professional mower (bottom-right) */}
      <g transform="translate(430 318) scale(0.9)" filter="url(#mkt-soft)">
        <rect x="8" y="36" width="96" height="30" rx="12" fill={C.green} />
        <rect x="24" y="12" width="44" height="32" rx="9" fill={C.paper} stroke={C.line} strokeWidth="2" />
        <rect x="30" y="18" width="32" height="14" rx="3" fill={C.sky} />
        <circle cx="28" cy="72" r="14" fill={C.anthracite} />
        <circle cx="28" cy="72" r="5" fill={C.line} />
        <circle cx="86" cy="72" r="14" fill={C.anthracite} />
        <circle cx="86" cy="72" r="5" fill={C.line} />
        <rect x="70" y="44" width="22" height="8" rx="2" fill={C.greenMid} />
      </g>

      {/* —— Central circular exchange symbol —— */}
      <g transform="translate(360 230)" filter="url(#mkt-soft)">
        {/* Outer glow disc */}
        <circle r="92" fill={C.green} opacity="0.12" />
        <circle r="78" fill={C.anthraciteSoft} opacity="0.92" stroke="url(#mkt-ring)" strokeWidth="5" />
        {/* Circular arrows */}
        <path
          d="M-42 -28 A52 52 0 0 1 42 -20"
          stroke={C.greenMid}
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
        />
        <path d="M34 -36 L52 -18 L28 -12 Z" fill={C.greenMid} />
        <path
          d="M42 28 A52 52 0 0 1 -42 20"
          stroke={C.green}
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
        />
        <path d="M-34 36 L-52 18 L-28 12 Z" fill={C.green} />
        {/* Center label */}
        <circle r="28" fill={C.green} />
        <text
          y="6"
          textAnchor="middle"
          fill={C.white}
          fontFamily="ui-sans-serif, system-ui, sans-serif"
          fontSize="13"
          fontWeight="800"
          letterSpacing="0.5"
        >
          ÉCHANGE
        </text>
      </g>
    </svg>
  );
}
